import { useCallback, useMemo } from 'react';

import type { ParsedInboxNotification } from '@aline/common';
import { safeParseInboxNotification } from '@aline/common';
import type { ApolloCache } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import { Apollo } from 'src/gql/generated';

export type InboxNotificationItem = Apollo.InboxNotificationSimpleFragment & {
  readonly parsed: ParsedInboxNotification;
};

/**
 * The viewer's inbox from the cache (fetched once, kept current by `useInboxNotificationsLive`).
 * Items whose payload this build cannot read are left out rather than shown half-rendered.
 */
export function useInboxNotifications() {
  const { data, loading } = Apollo.useGetInboxNotificationsQuery({ fetchPolicy: 'cache-first' });
  const items = useMemo(
    () =>
      (data?.inboxNotifications.items ?? []).flatMap(item => {
        const parsed = safeParseInboxNotification(item.kind, item.payload);
        return parsed ? [{ ...item, parsed }] : [];
      }),
    [data]
  );

  return {
    items,
    loading: loading && !data,
    unreadCount: data?.inboxNotifications.unreadCount ?? 0,
  };
}

/**
 * Keeps the inbox current: the server only signals that something changed, so each signal is
 * answered with a refetch. Mount once, in the header bell.
 */
export function useInboxNotificationsLive(): void {
  const refetchInbox = useRefetchInbox();
  Apollo.useOnInboxNotificationsChangedSubscription({
    ignoreResults: true,
    onData: () => void refetchInbox(),
  });
}

/** Re-reads the inbox every mounted bell and card is watching. */
export function useRefetchInbox() {
  const client = useApolloClient();
  return useCallback(
    () => client.refetchQueries({ include: [Apollo.GetInboxNotificationsDocument] }),
    [client]
  );
}

export function useInboxNotificationActions() {
  const [markRead] = Apollo.useMarkInboxNotificationsReadMutation({
    update: (cache, { data }) => {
      if (data) writeInbox(cache, data.markInboxNotificationsRead);
    },
  });
  const [markUnread] = Apollo.useMarkInboxNotificationsUnreadMutation({
    update: (cache, { data }) => {
      if (data) writeInbox(cache, data.markInboxNotificationsUnread);
    },
  });
  const [dismiss] = Apollo.useDismissInboxNotificationMutation({
    update: (cache, { data }) => {
      if (data) writeInbox(cache, data.dismissInboxNotification);
    },
  });

  const markAllRead = useCallback(
    (items: readonly Pick<InboxNotificationItem, 'id' | 'read'>[]) => {
      const ids = items.filter(item => !item.read).map(item => item.id);
      if (ids.length > 0) void markRead({ variables: { ids } });
    },
    [markRead]
  );
  const markOneUnread = useCallback(
    (id: string) => void markUnread({ variables: { ids: [id] } }),
    [markUnread]
  );
  const dismissOne = useCallback((id: string) => void dismiss({ variables: { id } }), [dismiss]);

  return { dismiss: dismissOne, markAllRead, markUnread: markOneUnread };
}

// The mutations answer with the whole inbox, which replaces the cached one wholesale.
function writeInbox(cache: ApolloCache, inbox: Apollo.InboxNotificationsResultSimpleFragment) {
  cache.writeQuery({
    data: { inboxNotifications: inbox },
    query: Apollo.GetInboxNotificationsDocument,
  });
}
